import { customIdOf, customKeyOf } from "@beacon/core/custom-emoji/names"
import { isUuid } from "@beacon/core/postgres/ids"
import type { ConversationKind } from "./index"

/*
 * What Chat accepts from a Member, decided without the record.
 */

/** Whether a Conversation of `kind` is a direct message, one-to-one or group. */
export const isDirect = (kind: ConversationKind) => kind === "dm" || kind === "group-dm"

/**
 * Where a message was said, as Beacon's own words name it to someone reading about it: a channel
 * by its name (`#plans`), a direct message by its kind alone, never by who is in it; "Chat" when
 * the place isn't known.
 */
export function placeWords(place: { kind: ConversationKind; name: string | null } | undefined) {
  switch (place?.kind) {
    case "public-channel":
    case "private-channel":
      return `#${place.name}`
    case "dm":
      return "a direct message"
    case "group-dm":
      return "a group direct message"
    default:
      return "Chat"
  }
}

/** The longest a channel's name may be. */
export const maxChannelName = 80

/** The most Members a direct message is with, besides the Member who starts it. */
export const maxDmOthers = 8

/** The longest a message may be, in characters. */
export const maxMessageLength = 40_000

/**
 * A channel's name as Chat keeps it: lower case, a leading `#` dropped, each run of spaces a
 * hyphen, and then only letters, digits, hyphens and underscores, 1 to `maxChannelName` long.
 * None for a name that can't be one.
 */
export function channelName(typed: string): string | undefined {
  const name = typed.trim().replace(/^#/, "").trim().toLowerCase().replace(/\s+/g, "-")
  if (name.length === 0 || name.length > maxChannelName) return undefined
  return /^[\p{Ll}\p{Lo}\p{N}_-]+$/u.test(name) ? name : undefined
}

/** The longest a channel's description may be, in characters. */
export const maxChannelDescription = 500

/**
 * A channel's description as Chat keeps it: plain text, trimmed, `null` when blank (none), and
 * `undefined` when it runs past `maxChannelDescription`.
 */
export function channelDescription(typed: string): string | null | undefined {
  const description = typed.trim()
  if (description.length === 0) return null
  return description.length > maxChannelDescription ? undefined : description
}

/** The most messages one Conversation keeps pinned. */
export const maxPins = 100

/** The most bookmarks one channel keeps. */
export const maxBookmarks = 50

/** The longest a bookmark's link may be, in characters. */
export const maxBookmarkUrl = 2000

/** The longest a bookmark's title may be, in characters. */
export const maxBookmarkTitle = 100

/**
 * A bookmark's link as Chat keeps it: an absolute `http` or `https` URL, as the URL standard writes
 * it, at most `maxBookmarkUrl` long, with no user name or password in it (which every reader would
 * read). None for anything else.
 */
export function bookmarkUrl(typed: string): string | undefined {
  const trimmed = typed.trim()
  if (trimmed.length === 0 || trimmed.length > maxBookmarkUrl) return undefined
  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    return undefined
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return undefined
  if (url.username || url.password || !url.hostname) return undefined
  return url.href.length > maxBookmarkUrl ? undefined : url.href
}

/**
 * A bookmark's title as Chat keeps it: plain text on one line, trimmed, `null` when blank (the link
 * names itself), and `undefined` when it runs past `maxBookmarkTitle`.
 */
export function bookmarkTitle(typed: string): string | null | undefined {
  const title = typed.replace(/\s+/g, " ").trim()
  if (title.length === 0) return null
  return title.length > maxBookmarkTitle ? undefined : title
}

/**
 * A message's body as sent, or none when it runs too long, or holds nothing while it carries no
 * file (`carriesFiles`): one that does may say nothing besides, and keeps an empty body.
 */
export function messageBody(typed: string, carriesFiles = false): string | undefined {
  if (typed.length > maxMessageLength) return undefined
  if (typed.trim().length === 0) return carriesFiles ? "" : undefined
  return typed
}

/** The most different emoji one message may be reacted to with. */
export const maxReactionEmoji = 50

/**
 * One emoji as Unicode builds it: a pictograph (with its presentation selector, skin tone and any
 * tag sequence, as a subdivision's flag has), a keycap, or a country's flag of two regional
 * indicators, and any of those joined into one by zero-width joiners (a family, a profession).
 * Pictographs are read by `Extended_Pictographic`, which reserves the code points future emoji
 * take, so an emoji newer than the server's Unicode data still reads as one.
 */
const element = String.raw`(?:\p{Regional_Indicator}{2}|[0-9#*]️?⃣|\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})*(?:[\u{E0020}-\u{E007E}]+\u{E007F})?)`
const standardEmoji = new RegExp(String.raw`^${element}(?:‍${element})*$`, "u")

/** The longest a reaction's emoji may be, in UTF-16 units: well past Unicode's longest emoji. */
const longestEmoji = 64

/**
 * A reaction's emoji as Chat keys it, or none when what was asked for isn't one: a single standard
 * emoji, as its Unicode, exactly as given (the picker gives each in its one fully qualified form),
 * or a custom emoji by its id (`custom:<id>`, `customKeyOf`), the id in lower case, which
 * `setReaction` then checks against the catalogue. Text, a shortcode or two emoji together are none.
 */
export function reactionEmoji(typed: unknown): string | undefined {
  if (typeof typed !== "string" || typed.length === 0 || typed.length > longestEmoji)
    return undefined
  const customId = customIdOf(typed)
  if (customId) return customKeyOf(customId)
  return standardEmoji.test(typed) ? typed : undefined
}

/**
 * Whether a value is an id Postgres can read: a malformed uuid is an error there rather than
 * nothing, so an id arriving in a request is checked first.
 */
export const isId = isUuid

/** A page size asked for, held to between 1 and `max`, `fallback` when none or unreadable. */
export function pageSize(asked: number | undefined, fallback: number, max: number) {
  if (asked === undefined || !Number.isInteger(asked) || asked < 1) return fallback
  return Math.min(asked, max)
}

/** A cursor a client sent back: a whole number at least `min`, or none when it isn't one. */
export function seqOf(value: number, min: number): number | undefined {
  return Number.isSafeInteger(value) && value >= min ? value : undefined
}

/** The largest file a Member may share in Chat: 100 MiB. A ticket's attachments keep their own. */
export const maxFileSize = 100 * 1024 * 1024

/** The most files one message may carry. */
export const maxFilesPerMessage = 10

const maxFileName = 255

/** A declared MD5 as hex, from hex or base64, or none when it is neither. */
function md5Of(declared: string) {
  const trimmed = declared.trim()
  if (/^[0-9a-f]{32}$/i.test(trimmed)) return trimmed.toLowerCase()
  if (/^[A-Za-z0-9+/]{22}==$/.test(trimmed)) return Buffer.from(trimmed, "base64").toString("hex")
  return undefined
}

/**
 * A file as a Member declares it before uploading it, as it is kept, or why it can't be:
 * `too-large` over `maxFileSize`, `invalid-file` for a name, a type, a size or an MD5 that isn't
 * one. The name is trimmed, the type kept lower case and the MD5 in hex.
 */
export function checkedFile({
  fileName,
  contentType,
  size,
  md5,
}: {
  fileName: string
  contentType: string
  size: number
  md5: string
}):
  | { ok: true; value: { fileName: string; contentType: string; size: number; md5: string } }
  | { ok: false; refusal: "too-large" | "invalid-file" } {
  const name = fileName.trim()
  const type = contentType.trim().toLowerCase()
  const digest = md5Of(md5)
  if (
    !name ||
    name.length > maxFileName ||
    // Control characters would reach a header when the file is downloaded.
    /\p{Cc}/u.test(name) ||
    !/^[\w.+-]+\/[\w.+-]+$/.test(type) ||
    !Number.isSafeInteger(size) ||
    size <= 0 ||
    !digest
  )
    return { ok: false, refusal: "invalid-file" }
  if (size > maxFileSize) return { ok: false, refusal: "too-large" }
  return { ok: true, value: { fileName: name, contentType: type, size, md5: digest } }
}

/** The image types a browser is handed to show, once a file's own bytes say it is one. */
export const previewableTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"] as const

/** How many of a file's first bytes `sniffedImage` reads. */
export const sniffLength = 16

const startsWith = (bytes: Uint8Array, at: number, expected: readonly (number | string)[]) => {
  const wanted = expected.flatMap((value) =>
    typeof value === "string" ? [...value].map((c) => c.charCodeAt(0)) : [value],
  )
  return wanted.every((byte, index) => bytes[at + index] === byte)
}

/**
 * Which of `previewableTypes` a file's first bytes say it is, by its signature, none for anything
 * else (an SVG, a page, a script named as an image among them).
 */
export function sniffedImage(bytes: Uint8Array): (typeof previewableTypes)[number] | undefined {
  if (startsWith(bytes, 0, [0x89, "PNG", 0x0d, 0x0a, 0x1a, 0x0a])) return "image/png"
  if (startsWith(bytes, 0, [0xff, 0xd8, 0xff])) return "image/jpeg"
  if (startsWith(bytes, 0, ["GIF87a"]) || startsWith(bytes, 0, ["GIF89a"])) return "image/gif"
  if (startsWith(bytes, 0, ["RIFF"]) && startsWith(bytes, 8, ["WEBP"])) return "image/webp"
  return undefined
}

/** The longest a status's words may be. */
export const maxStatusText = 100

/** The longest a status's emoji may be: one emoji, however many code points, or a `:shortcode:`. */
const maxStatusEmoji = 64

/** How far ahead a status may be set to clear. */
const longestStatus = 366 * 24 * 60 * 60_000

/** A status as Chat keeps it: its emoji, its words (either may be none), and when it clears. */
export type StatusSet = { emoji: string | null; text: string | null; until: Date | null }

/**
 * A status as a Member set it, or none when it can't be one: it holds an emoji (no spaces, at most
 * 64 characters), words (each run of white space one space, at most `maxStatusText`) or both, and
 * clears at `until` when given, which must be an instant after `now` and within a year of it.
 */
export function statusOf(
  typed: { emoji: string | null; text: string | null; until: string | null },
  now: Date,
): StatusSet | undefined {
  const emoji = typed.emoji?.trim() || null
  const text = typed.text?.trim().replace(/\s+/g, " ") || null
  if (!emoji && !text) return undefined
  if (emoji && (emoji.length > maxStatusEmoji || /\s/.test(emoji))) return undefined
  if (text && text.length > maxStatusText) return undefined
  if (typed.until === null) return { emoji, text, until: null }
  const until = new Date(typed.until)
  const ahead = until.getTime() - now.getTime()
  if (!/^\d{4}-\d{2}-\d{2}T/.test(typed.until) || Number.isNaN(ahead)) return undefined
  if (ahead <= 0 || ahead > longestStatus) return undefined
  return { emoji, text, until }
}
